import { useState, useEffect, useCallback } from 'react'
import { fetchMonthlyAttendance } from '../api/monthlyAttendanceApi'
import { appToast } from '../../../../components/AppToast'

export default function useMonthlyAttendance() {
  const [monthKey, setMonthKey] = useState(() => {
    const d = new Date()
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    return `${y}-${m}`
  })

  const [daysMeta, setDaysMeta] = useState([])
  const [students, setStudents] = useState([])
  const [loading, setLoading] = useState(false)
  const [isReportsOpen, setIsReportsOpen] = useState(false)

  // Filters
  const [searchText, setSearchText] = useState('')
  const [searchField, setSearchField] = useState('All')
  const [companyFilter, setCompanyFilter] = useState('All')
  const [branchFilter, setBranchFilter] = useState('All')
  const [courseFilter, setCourseFilter] = useState('All')
  const [schoolFilter, setSchoolFilter] = useState('All')
  const [academicYearFilter, setAcademicYearFilter] = useState('All')
  const [batchFilter, setBatchFilter] = useState('All')
  const [subjectFilter, setSubjectFilter] = useState('All')
  const [orderBy, setOrderBy] = useState('1') // 1 = Name, 2 = Roll No

  // Metrics
  const [metrics, setMetrics] = useState({
    totalStudents: 0,
    averagePercentage: 0,
    workingDays: 0,
    totalDays: 31,
    defaultersCount: 0,
  })

  // Load Data
  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const filters = {
        searchText,
        searchField,
        company: companyFilter,
        branch: branchFilter,
        course: courseFilter,
        school: schoolFilter,
        academicYear: academicYearFilter,
        batch: batchFilter,
        subject: subjectFilter,
      }
      const res = await fetchMonthlyAttendance(monthKey, filters, orderBy)
      setDaysMeta(res.daysMeta)
      setStudents(res.students)
      setMetrics(res.metrics)
    } catch (e) {
      console.error('Failed to load monthly attendance', e)
      appToast.error('Failed to load monthly attendance matrix.')
    } finally {
      setLoading(false)
    }
  }, [
    monthKey,
    searchText,
    searchField,
    companyFilter,
    branchFilter,
    courseFilter,
    schoolFilter,
    academicYearFilter,
    batchFilter,
    subjectFilter,
    orderBy,
  ])

  useEffect(() => {
    loadData()
  }, [loadData])

  // Month Navigation Helpers
  const handlePrevMonth = () => {
    const [y, m] = monthKey.split('-').map(Number)
    const prevDate = new Date(y, m - 2, 1)
    const newY = prevDate.getFullYear()
    const newM = String(prevDate.getMonth() + 1).padStart(2, '0')
    setMonthKey(`${newY}-${newM}`)
  }

  const handleNextMonth = () => {
    const [y, m] = monthKey.split('-').map(Number)
    const nextDate = new Date(y, m, 1)
    const newY = nextDate.getFullYear()
    const newM = String(nextDate.getMonth() + 1).padStart(2, '0')
    setMonthKey(`${newY}-${newM}`)
  }

  const handleCurrentMonth = () => {
    const d = new Date()
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    setMonthKey(`${y}-${m}`)
  }

  const handleLastMonth = () => {
    const d = new Date()
    d.setMonth(d.getMonth() - 1)
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    setMonthKey(`${y}-${m}`)
  }

  // Reset Filters
  const handleResetFilters = () => {
    setSearchText('')
    setSearchField('All')
    setCompanyFilter('All')
    setBranchFilter('All')
    setCourseFilter('All')
    setSchoolFilter('All')
    setAcademicYearFilter('2026-2027')
    setBatchFilter('All')
    setSubjectFilter('All')
    setOrderBy('1')
    appToast.info('Filters have been reset.')
  }

  // Export Matrix
  const handleExportExcel = () => {
    appToast.success(`Exporting Monthly Attendance Matrix for ${monthKey}...`)
  }

  // SMS & WhatsApp Alerts
  const handleSendSMS = () => {
    appToast.info(`Dispatched monthly attendance summaries via SMS to parents.`)
  }

  const handleSendWhatsApp = () => {
    appToast.info(`Dispatched monthly compliance cards to parents via WhatsApp.`)
  }

  // Options
  const options = {
    searchFields: [
      { value: 'All', label: 'All Fields' },
      { value: 'Student', label: 'Student Name' },
      { value: 'RollNo', label: 'Roll Number' },
      { value: 'Course', label: 'Course' },
      { value: 'Batch', label: 'Batch' },
      { value: 'Branch', label: 'Branch' },
      { value: 'Company', label: 'Company' },
    ],
    companies: [
      { value: 'All', label: 'All Companies' },
      { value: 'Tez Educational Trust', label: 'Tez Educational Trust' },
      { value: 'Tez Educational School', label: 'Tez Educational School' },
      { value: 'Tolani College Of Commerce', label: 'Tolani College Of Commerce' },
    ],
    branches: [
      { value: 'All', label: 'All Branches' },
      { value: 'Dadar', label: 'Dadar' },
      { value: 'Thane', label: 'Thane' },
      { value: 'Andheri', label: 'Andheri' },
      { value: 'Borivali', label: 'Borivali' },
    ],
    courses: [
      { value: 'All', label: 'All Courses' },
      { value: 'CA Foundation', label: 'CA Foundation' },
      { value: 'CS Executive', label: 'CS Executive' },
      { value: 'JEE Advanced', label: 'JEE Advanced' },
      { value: 'NEET UG', label: 'NEET UG' },
    ],
    schools: [
      { value: 'All', label: 'All Schools' },
      { value: 'Main Campus', label: 'Main Campus' },
      { value: 'East Annex', label: 'East Annex' },
      { value: 'North Campus', label: 'North Campus' },
    ],
    batches: [
      { value: 'All', label: 'All Batches' },
      { value: 'Batch-A (Morning)', label: 'Batch-A (Morning)' },
      { value: 'Batch-CS-01', label: 'Batch-CS-01' },
      { value: 'Super-30 (Intensive)', label: 'Super-30 (Intensive)' },
      { value: 'Med-Achievers', label: 'Med-Achievers' },
    ],
    subjects: [
      { value: 'All', label: 'All Subjects' },
      { value: 'Principles and Practice of Accounting', label: 'Accounting' },
      { value: 'Company Law & Practice', label: 'Company Law' },
      { value: 'Physics', label: 'Physics' },
      { value: 'Mathematics', label: 'Mathematics' },
      { value: 'Biology (Botany)', label: 'Biology' },
      { value: 'Chemistry', label: 'Chemistry' },
    ],
    academicYears: [
      { value: 'All', label: 'All Years' },
      { value: '2026-2027', label: '2026-2027' },
      { value: '2025-2026', label: '2025-2026' },
    ],
  }

  return {
    state: {
      monthKey,
      daysMeta,
      students,
      metrics,
      loading,
      isReportsOpen,
      searchText,
      searchField,
      companyFilter,
      branchFilter,
      courseFilter,
      schoolFilter,
      academicYearFilter,
      batchFilter,
      subjectFilter,
      orderBy,
      options,
    },
    actions: {
      setMonthKey,
      setIsReportsOpen,
      setSearchText,
      setSearchField,
      setCompanyFilter,
      setBranchFilter,
      setCourseFilter,
      setSchoolFilter,
      setAcademicYearFilter,
      setBatchFilter,
      setSubjectFilter,
      setOrderBy,
      handlePrevMonth,
      handleNextMonth,
      handleCurrentMonth,
      handleLastMonth,
      handleResetFilters,
      handleExportExcel,
      handleSendSMS,
      handleSendWhatsApp,
    },
  }
}
